import React, { useEffect, useState } from "react";
import Modal, { modalStyles as m } from "./Modal";
import { phpQuickAddProduct } from "../data/api";
import { getQuickAddCategoryId, isDuplicateCode, findByCode } from "../data/queries";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";
import { validQuickAddProduct } from "../validators";

// Quick-Add — create a REAL catalog product from the register (Android
// `quickAddNewProduct` → Productapi/quick_add_product_new).
//
// ⚠️ This is NOT the same thing as a Custom Item. A custom item is a one-off cart line with no
// catalog record; a quick-add product is persisted, scannable afterwards, and (since Android's
// 2026-08-05 change) eligible for manufacturer discounts like any other line — which is exactly why
// a real, unique UPC is mandatory here.
//
// Flow, in Android's order: duplicate-UPC pre-check against the local catalog → field validation →
// resolve the store's "Quickadd" category → create → pull the new product into the local catalog and
// drop it in the cart.
export default function QuickAddProductModal({ initialName = "", initialUpc = "", taxMap, onAdded, onClose }) {
  const [title, setTitle] = useState(initialName);
  const [price, setPrice] = useState("");
  const [upc, setUpc] = useState(initialUpc);
  const [taxIds, setTaxIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [catId, setCatId] = useState(null); // null = still resolving

  useEffect(() => {
    getQuickAddCategoryId().then(setCatId).catch(() => setCatId(""));
  }, []);

  const allTaxes = Array.from(taxMap ? taxMap.values() : []).filter((x) => x && x.id != null && String(x.id).trim() !== "");
  const toggleTax = (id) =>
    setTaxIds((prev) => (prev.includes(String(id)) ? prev.filter((x) => x !== String(id)) : [...prev, String(id)]));

  // Android's "generate" button: 12 random digits, only when the field is empty.
  const generateUpc = () => {
    if (String(upc).trim()) return;
    let out = "";
    for (let i = 0; i < 12; i++) out += Math.floor(Math.random() * 10);
    setUpc(out);
  };

  const submit = async () => {
    const code = String(upc).trim();
    const check = validQuickAddProduct({ title, price, upc: code });
    if (!check.ok) return ToastifyAlert(check.message, "error");
    if (!catId) return ToastifyAlert("QuickAdd category is missing — create a category named \"Quickadd\" first.", "error");

    setBusy(true);
    try {
      // Pre-check BEFORE creating, so the cashier sees the clash on the field rather than a server
      // rejection after the fact. Catalog codes are unique across product UPC, variant UPC and
      // custom code, so all three are searched.
      if (await isDuplicateCode(code)) {
        setBusy(false);
        return ToastifyAlert("Duplicate UPC found — this code is already in the catalog.", "error");
      }
      const res = await phpQuickAddProduct({
        title: title.trim(),
        price: String(price).trim(),
        tax: taxIds.join(","),
        upc: code,
        categoryId: catId,
      });
      if (!res || !(res.status === true || res.status === "true")) {
        setBusy(false);
        // The only way a validated quick-add fails server-side is a UPC that already exists (the local
        // pre-check missed it because the catalog cache was stale). Android reports that as "product
        // already exist", but this endpoint answers with a bare "failed" — surface the meaningful
        // message instead of the raw token (keep any informative server message as-is).
        const raw = String((res && res.message) || "").trim();
        const uninformative = raw === "" || /^(failed?|error|false|0)$/i.test(raw);
        return ToastifyAlert(uninformative ? "Product already exists" : raw, "error");
      }
      ToastifyAlert("Product added", "success");
      // Hand the caller the created identity; it owns pulling the product into the local catalog and
      // adding it to the cart (the register needs the synced row to build a proper cart line).
      if (onAdded) await onAdded({ productId: String(res.product_id || ""), upc: code, title: title.trim(), price: String(price).trim() });
      onClose();
    } catch (e) {
      ToastifyAlert("Could not add the product: " + (e && e.message ? e.message : e), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Quick Add product" onClose={onClose} width={460} zIndex={2100}>
      <div style={m.label}>Product name *</div>
      <input autoFocus style={m.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Product name" />

      <div style={{ ...m.label, marginTop: 10 }}>Selling price *</div>
      <input
        style={m.input}
        value={price}
        inputMode="decimal"
        onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ""))}
        placeholder="0.00"
      />

      <div style={{ ...m.label, marginTop: 10 }}>UPC *</div>
      <div style={{ display: "flex", gap: 8 }}>
        <input style={{ ...m.input, flex: 1 }} value={upc} onChange={(e) => setUpc(e.target.value.trim())} placeholder="Scan or type a UPC" />
        <button style={{ ...m.ghost, width: 130, marginTop: 0 }} onClick={generateUpc} disabled={busy}>
          Generate
        </button>
      </div>

      {allTaxes.length > 0 && (
        <>
          <div style={{ ...m.label, marginTop: 12 }}>Taxes</div>
          <div style={{ maxHeight: 150, overflow: "auto" }}>
            {allTaxes.map((tax) => {
              const on = taxIds.includes(String(tax.id));
              return (
                <button
                  key={String(tax.id)}
                  onClick={() => toggleTax(tax.id)}
                  style={{
                    display: "block", width: "100%", textAlign: "left", padding: "10px 12px", marginBottom: 6,
                    borderRadius: 10, cursor: "pointer", background: "var(--panel-2)",
                    border: `1px solid ${on ? "var(--accent)" : "var(--line)"}`, color: on ? "var(--accent)" : "var(--ink)",
                  }}
                >
                  {on ? "☑" : "☐"} {tax.displayname || tax.title}
                  {tax.percent ? ` (${String(tax.percent).trim()}%)` : ""}
                </button>
              );
            })}
          </div>
        </>
      )}

      {catId === "" && (
        <div style={{ marginTop: 10, color: "#DC2626", fontSize: 13 }}>
          This store has no “Quickadd” category, so a product can’t be created from the register.
        </div>
      )}

      <button style={{ ...m.primary, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={submit}>
        {busy ? "Adding…" : "Add product"}
      </button>
      <button style={m.ghost} onClick={onClose} disabled={busy}>
        Cancel
      </button>
    </Modal>
  );
}

// Pull a just-created product into the local catalog so it can be added to the cart immediately.
// The register reads its catalog from IndexedDB, which only refreshes on the background delta sync,
// so a freshly created product would otherwise be invisible until the next poll. Polls for it (the
// sync may already be in flight) and gives up quietly rather than blocking the sale.
export async function waitForQuickAddedProduct(upc, { attempts = 6, delayMs = 500, sync } = {}) {
  for (let i = 0; i < attempts; i++) {
    const hit = await findByCode(upc);
    if (hit) return hit;
    if (sync) await sync().catch(() => {});
    await new Promise((r) => setTimeout(r, delayMs));
  }
  return await findByCode(upc);
}
